import { h, Component } from 'preact';
import Store from 'store/store';
import { joinClasses } from 'utils/utils';
import { getEditorConfigFromURL, getMovieData } from 'utils/editor';
import { canUser } from 'utils/roles';
import { isPlaceholder } from 'utils/data';
import { mergeInto } from 'utils/merge';
import * as jsonPointer from 'utils/jsonPointer';
import { UndoRedoManager } from 'utils/undoRedo';
import { isBaseRegion } from 'utils/studioConfig';

import EditorSidebar from 'components/editor/sidebar';
import Button from 'components/button/button';
import SaveButton from 'components/button/saveButton';
import PreviewButton from 'components/button/previewButton';
import PublishButton from 'components/button/publishButton';
import VersionHistoryButton from 'components/button/versionHistoryButton';
import UndoRedoButtons from 'components/button/undoRedoButtons';
import Spinner from 'components/spinner/spinner';

import s from 'components/editor/editor.sss';

export default class Editor extends Component {

	get isBase() {
		return isBaseRegion();
	}

	constructor(props) {
		super(props);

		this.state = {
			settingsCollapsed: false,
			activeElement: null,
			config: null,
		};

		this.getActiveDataPath = this.getActiveDataPath.bind(this);
		this.selectElement = this.selectElement.bind(this);
		this.onContentClick = this.onContentClick.bind(this);
		this.toggleSettings = this.toggleSettings.bind(this);

		this.updateData(props);
		this.checkActiveSection(props);

		this.undoRedo = new UndoRedoManager({
			dataPath: this.getActiveDataPath
		});
	}

	componentWillReceiveProps(nextProps) {
		// Recalculate config when placeholder status changes or when region changes (movie ID changes)
		const placeholderStatusChanged = this.isPlaceholder(nextProps) !== !!this.state.config?.placeholder;
		const movieIdChanged = getEditorConfigFromURL(nextProps.path)?.id !== this.state.config?.id;
		if (placeholderStatusChanged || movieIdChanged) {
			this.updateData(nextProps);
			this.checkActiveSection(nextProps);
		}
		if (nextProps.path?.join('/') !== this.props.path?.join('/')) {
			this.setState({ activeElement: null });
		}
		if (this.props.path !== nextProps.path && this.state.config) {
			this.checkActiveSection(nextProps);
		}
	}

	componentWillUnmount() {
		this.undoRedo?.dispose();
	}

	exit() {
		location.hash = this.props.path[0];
	}

	isPlaceholder(props = this.props) {
		return props.path[0] === 'movies' && isPlaceholder(getMovieData(props.path[1]));
	}

	getActiveDataPath() {
		return jsonPointer.concat(this.getActiveSection()?.dataRoot, '/content');
	}

	checkActiveSection(props = this.props) {
		const config = this.state.config;
		if (!config) return;
		if (!this.getActiveSection(props)) {
			const topSection = config.sections?.[0];
			const link = topSection ? `#${config.basePath}/${topSection.path}` : `#${props.path[0]}`;
			window.location.hash = link;
		}
	}

	updateData(props = this.props) {
		const config = getEditorConfigFromURL(props.path);
		if (!config) return this.exit();
		// if (!config) return;
		if (config.id !== this.state.config?.id || config.placeholder !== this.state.config?.placeholder) {
			this.setState({ config });
		}
	}

	selectElement(path) {
		if (this.state.activeElement !== path) {
			this.setState({ activeElement: path });
		}
	}

	onContentClick(e) {
		const clicked = e.target.closest('[data-section]')?.dataset.section || null;
		if (clicked === '#') return;
		const desired = clicked === this.state.activeElement ? null : clicked;
		if (this.state.activeElement !== desired) {
			this.setState({ activeElement: desired });
		}
	}

	getActiveSection(props = this.props) {
		const currentPath = props.path.join('/');
		const config = this.state.config;
		return config?.sections.find(section => currentPath.startsWith(`${config.basePath}/${section.path}`));
	}

	getContentComponentProps(activeSection = this.getActiveSection()) {
		const dataRoot = activeSection?.dataRoot || '';
		const data = jsonPointer.get(this.props.data, dataRoot);
		const isMoviePage = dataRoot?.startsWith('/movies');

		let content = structuredClone(data?.content);

		if (isMoviePage && content) {
			// Merge if locale has footer content to share across all movies
			const localeFooter = jsonPointer.get(Store.get().data, '/navigation/content/moviePageFooter');
			if (localeFooter) {
				if (!content.footer) content.footer = {};
				// Need to specifically target the footer links and copyright otherwise the merge function will remove the soft-deleted logos
				content.footer.links = mergeInto(localeFooter.links, content.footer.links);
				content.footer.copyright = mergeInto(localeFooter.copyright, content.footer.copyright);
			}

			// If has base_movie, ensure content in merged for use in the visualisation
			const baseContent = data?.base_movie?.content;
			if (baseContent) {
				// Merge base_movie content into the movie content, but don't overwrite existing values
				content = mergeInto(baseContent, content);
			}
		}

		return {
			config: this.state.config,
			activeSection: activeSection,
			path: this.props.path,
			activeElement: this.state.activeElement,
			dataRoot,
			data,
			content, // For use in the visualisation
			select: this.selectElement,
		};
	}

	toggleSettings() {
		this.setState({ settingsCollapsed: !this.state.settingsCollapsed });
	}

	renderToolbar(activeSection = this.getActiveSection()) {
		const dataPath = activeSection?.dataRoot;
		const isMoviePage = dataPath?.startsWith('/movies');
		const topLevelPath = this.props.path[0];

		const canEdit = canUser('write');
		const canPreview = !this.isBase;
		const canPublish = !this.isBase && canUser('publish');
		const allowUnpublish = !!isMoviePage && !isPlaceholder(jsonPointer.get(this.props.data, dataPath));

		return (
			<div class={s.toolbar} key="toolbar">
				<div class={s.left}>
					<Button dark icon="chevron-left" class={s.exit} href={`#${topLevelPath}`}>Exit editor</Button>
					{canEdit && <UndoRedoButtons dark manager={this.undoRedo} key="undo-redo" />}
				</div>
				<div class={s.right}>
					<VersionHistoryButton dark path={dataPath} key="history" />
					{canEdit && <SaveButton dark path={dataPath} key="save" />}
					{canPreview && <PreviewButton dark path={dataPath} key="preview" />}
					{canPublish && <PublishButton dark path={dataPath} key="publish" allowUnpublish={allowUnpublish} />}
				</div>
			</div>
		);
	}

	renderSidebar(activeSection = this.getActiveSection()) {
		const config = this.state.config;
		if (!config) return <div class={s.sidebar} key="sidebar" />;
		const Component = config?.sidebar || EditorSidebar;
		return (
			<Component
				activeSection={activeSection}
				config={this.state.config}
				data={this.props.data}
				loading={this.props.loading}
				path={this.props.path}
			/>
		);
	}

	renderContent(activeSection = this.getActiveSection()) {
		const { loading } = this.props;
		const isFocus = Store.get().domains?.isFocus;
		const Component = activeSection?.content;
		const showContent = !loading && !!Component;

		return (
			<main class={joinClasses(s.content, isFocus && s.focus, loading && s.loading)} onClickCapture={this.onContentClick}>
				{loading && <Spinner />}
				{showContent && <Component visual {...this.getContentComponentProps(activeSection)} />}
			</main>
		);
	}

	renderSettings(activeSection = this.getActiveSection()) {
		if (!this.state.activeElement) return <div class={s.settings} key="settings" />;
		const Component = activeSection?.content;
		return (
			<div class={joinClasses(s.settings, this.state.settingsCollapsed && s.collapsed)} key="settings">
				<Button dark icon="sidebar-collapse" class={s.toggle} onClick={this.toggleSettings} />
				<div class={s.inner}>
					<h2 class={s.title}>Settings</h2>
					{!!Component && <Component settings {...this.getContentComponentProps(activeSection)} />}
				</div>
			</div>
		);
	}

	render() {
		const activeSection = this.getActiveSection();
		if (!activeSection) return <div class={s.wrapper} dark />;
		return (
			<div class={s.wrapper} dark>
				{this.renderToolbar(activeSection)}
				{this.renderSidebar(activeSection)}
				{this.renderContent(activeSection)}
				{this.renderSettings(activeSection)}
			</div>
		);
	}
}